import LegalLayout, { LegalH2 } from "../components/LegalLayout";
import useSEO from "../hooks/useSEO";
import { LEGAL, CONTACT } from "../config";

export default function PrivacyPolicy() {
  useSEO({
    title: "Privacy Policy | Muratore Imbianchino Roma",
    description: "Informativa sul trattamento dei dati personali ai sensi del Regolamento (UE) 2016/679 (GDPR).",
    path: "/privacy",
  });

  return (
    <LegalLayout title="Privacy Policy" updated={LEGAL.updated}>
      <p>
        La presente informativa descrive le modalità di trattamento dei dati personali
        degli utenti che consultano il sito e che inviano richieste di preventivo o di
        contatto, ai sensi del Regolamento (UE) 2016/679 ("GDPR") e della normativa
        italiana applicabile.
      </p>

      <LegalH2>1. Titolare del trattamento</LegalH2>
      <p>Il Titolare del trattamento è:</p>
      <ul className="list-disc pl-5 space-y-1">
        <li><strong>{LEGAL.owner}</strong></li>
        <li>{LEGAL.taxIdLabel}: {LEGAL.taxId}</li>
        <li>Sede: {LEGAL.address}</li>
        <li>Email di contatto per la privacy: <a className="text-navy underline underline-offset-2" href={`mailto:${LEGAL.privacyEmail}`}>{LEGAL.privacyEmail}</a></li>
      </ul>

      <LegalH2>2. Tipologie di dati raccolti</LegalH2>
      <p>Trattiamo le seguenti categorie di dati:</p>
      <ul className="list-disc pl-5 space-y-1">
        <li><strong>Dati forniti volontariamente</strong> tramite il modulo di richiesta preventivo: nome (facoltativo), numero di telefono/WhatsApp, indirizzo email, zona di Roma, tipologia e descrizione del lavoro ed eventuali fotografie che decidi di allegare.</li>
        <li><strong>Dati di contatto</strong> quando ci scrivi via email, WhatsApp o telefono.</li>
        <li><strong>Dati di navigazione e tecnici</strong> (es. indirizzo IP, tipo di browser e dispositivo, sistema operativo, pagine visitate, data e ora) raccolti tramite strumenti di analisi, solo previo consenso.</li>
        <li><strong>Dati di provenienza della campagna</strong> (parametri UTM, referrer, eventuali identificativi come gclid/fbclid) utili a capire come sei arrivato al sito.</li>
      </ul>

      <LegalH2>3. Finalità e base giuridica</LegalH2>
      <ul className="list-disc pl-5 space-y-1">
        <li><strong>Rispondere alle richieste di preventivo/contatto e gestire il rapporto</strong> — base giuridica: esecuzione di misure precontrattuali richieste dall'interessato (art. 6.1.b GDPR).</li>
        <li><strong>Gestione delle richieste in un sistema CRM interno</strong> — base giuridica: legittimo interesse del Titolare a organizzare e dare seguito alle richieste (art. 6.1.f GDPR).</li>
        <li><strong>Statistiche e analisi</strong> sull'uso del sito — base giuridica: consenso (art. 6.1.a GDPR).</li>
        <li><strong>Marketing e remarketing</strong> tramite piattaforme pubblicitarie — base giuridica: consenso (art. 6.1.a GDPR).</li>
        <li><strong>Adempimenti di legge</strong> (fiscali, amministrativi) — base giuridica: obbligo legale (art. 6.1.c GDPR).</li>
      </ul>
      <p>Il conferimento dei dati di contatto è facoltativo, ma necessario per poter ricevere ed elaborare la tua richiesta di preventivo.</p>

      <LegalH2>4. Modalità del trattamento</LegalH2>
      <p>
        I dati sono trattati con strumenti elettronici, adottando misure tecniche e
        organizzative adeguate a garantirne sicurezza e riservatezza e a prevenire
        accessi non autorizzati, perdita o divulgazione.
      </p>

      <LegalH2>5. Destinatari e responsabili del trattamento</LegalH2>
      <p>I dati possono essere trattati, per nostro conto, da fornitori che agiscono come responsabili del trattamento, tra cui:</p>
      <ul className="list-disc pl-5 space-y-1">
        <li>fornitore di hosting e infrastruttura del sito;</li>
        <li>fornitore del servizio di invio email (notifiche e conferme);</li>
        <li>Google (Google Analytics e Google Ads), solo previo consenso ai relativi cookie;</li>
        <li>Meta Platforms (Meta Pixel), solo previo consenso ai relativi cookie;</li>
        <li>WhatsApp/Meta, quando scegli di contattarci tramite WhatsApp.</li>
      </ul>
      <p>I dati non sono diffusi né ceduti a terzi per finalità diverse da quelle indicate.</p>

      <LegalH2>6. Trasferimento dei dati extra-UE</LegalH2>
      <p>
        Alcuni fornitori (es. Google, Meta) possono trattare i dati anche al di fuori
        dello Spazio Economico Europeo. In tali casi il trasferimento avviene sulla base
        di garanzie adeguate previste dal GDPR, quali le Clausole Contrattuali Standard
        della Commissione Europea o meccanismi di adeguatezza riconosciuti.
      </p>

      <LegalH2>7. Periodo di conservazione</LegalH2>
      <p>
        I dati relativi alle richieste sono conservati per il tempo necessario a gestire
        la richiesta e l'eventuale rapporto che ne consegue, e successivamente per il
        periodo richiesto da obblighi di legge. I dati raccolti tramite cookie sono
        conservati per la durata indicata nella <a className="text-navy underline underline-offset-2" href="/cookie-policy">Cookie Policy</a>.
      </p>

      <LegalH2>8. Diritti dell'interessato</LegalH2>
      <p>In qualsiasi momento puoi esercitare i diritti previsti dagli artt. 15-22 del GDPR:</p>
      <ul className="list-disc pl-5 space-y-1">
        <li>accesso ai tuoi dati e loro rettifica o cancellazione;</li>
        <li>limitazione del trattamento e opposizione al trattamento;</li>
        <li>portabilità dei dati;</li>
        <li>revoca del consenso in qualsiasi momento, senza pregiudicare la liceità del trattamento basata sul consenso prestato prima della revoca;</li>
        <li>proporre reclamo all'Autorità Garante per la protezione dei dati personali (<a className="text-navy underline underline-offset-2" href="https://www.garanteprivacy.it" target="_blank" rel="noopener noreferrer">www.garanteprivacy.it</a>).</li>
      </ul>
      <p>
        Per esercitare i tuoi diritti scrivi a{" "}
        <a className="text-navy underline underline-offset-2" href={`mailto:${LEGAL.privacyEmail}`}>{LEGAL.privacyEmail}</a>.
      </p>

      <LegalH2>9. Cookie</LegalH2>
      <p>
        Il sito utilizza cookie tecnici e, previo consenso, cookie di analisi e di
        marketing. Per il dettaglio e per gestire le tue preferenze consulta la{" "}
        <a className="text-navy underline underline-offset-2" href="/cookie-policy">Cookie Policy</a>.
      </p>

      <LegalH2>10. Modifiche</LegalH2>
      <p>
        Il Titolare si riserva di aggiornare la presente informativa. Le modifiche
        saranno pubblicate su questa pagina con l'indicazione della data di ultimo
        aggiornamento. Sito: {CONTACT.businessName}.
      </p>
    </LegalLayout>
  );
}
